import { useQuery } from "@tanstack/react-query";
import { http } from "@/lib/api";

const isIp = (h) => /^\d{1,3}(\.\d{1,3}){3}$/.test((h || "").split(":")[0]);

// SINGLE SOURCE OF TRUTH for tracking domains offered in Offer/Stream creation.
// Platform domains come from the current Primary Platform Domain (active_platform_domain) —
// never from legacy fields or a stale/raw IP. A dedicated Base Tracking Domain (a real, non-IP
// subdomain) is offered as an extra option. The tenant's own verified tracking domains are then
// merged in. Default selection is always the current Primary.
export function usePlatformTrackingDomains() {
  const { data: pub } = useQuery({ queryKey: ["platform-public"], queryFn: async () => (await http.get("/platform-settings/public")).data, staleTime: 0 });
  const { data: mine } = useQuery({ queryKey: ["domains"], queryFn: async () => (await http.get("/domains")).data, staleTime: 0 });

  const primary = (pub?.active_platform_domain
    || (pub?.platform_origin || "").replace(/^https?:\/\//, "").replace(/\/$/, "")).toLowerCase();
  const btd = (pub?.base_tracking_domain || "").toLowerCase();
  const dedicated = btd && !isIp(btd) && btd !== primary ? btd : "";

  const platformOptions = [primary, dedicated].filter(Boolean);
  const tenantTracking = (mine?.items || []).filter((d) => (d.usages || []).includes("tracking") && d.status === "active");
  const tenantOptions = tenantTracking.map((d) => (d.domain || "").toLowerCase());
  // A tenant's default tracking domain (Make default) wins over the platform Primary for new links.
  const tenantDefault = (tenantTracking.find((d) => (d.defaults || {}).tracking)?.domain || "").toLowerCase();
  const defaultDomain = tenantDefault || primary;

  const options = Array.from(new Set([...platformOptions, ...tenantOptions].filter(Boolean)));
  return {
    primary,
    defaultDomain,
    tenantDefault,
    platformOrigin: (pub?.platform_origin || "").replace(/\/$/, ""),
    dedicated,
    platformOptions,
    tenantOptions,
    options,
    hasTenantDomains: tenantOptions.length > 0,
    // Preview of a stream tracking link for a given domain + key.
    linkFor: (domain, key) => {
      const d = (domain || "").toLowerCase();
      if (dedicated && d === dedicated) return `https://${d}/${key}`;
      if (d && d !== primary && tenantOptions.includes(d)) return `https://${d}/api/r/${key}`;
      return `${(pub?.platform_origin || "").replace(/\/$/, "")}/api/r/${key}`;
    },
  };
}
